import { App as CapacitorApp } from '@capacitor/app';

import { isStoreBuild } from './platform';

/**
 * Управление обновлениями с сервера: «обновись» и «выйди заново».
 *
 * ⚠️ ГЛАВНОЕ ПРАВИЛО, РАДИ КОТОРОГО ВСЁ ЭТО: СЕРВЕР ОБЯЗАН ПОНИМАТЬ И СТАРЫЕ
 * ПРИЛОЖЕНИЯ. Новое поле, новый заголовок, новый ответ — всё необязательное.
 * 01.09.2026 правило нарушили: приложение начало слать заголовок, которого
 * сервер не разрешал, и ОБНОВИВШИЕСЯ остались без входа, а не обновившиеся
 * работали. Здесь — страховка на случай, когда совместимость всё-таки сломана:
 * закрыть дыру можно с сервера, не дожидаясь недели проверки в App Store.
 */

/**
 * Ниже ли версия приложения, чем требует сервер.
 *
 * ⚠️ ЧИСЛА СРАВНИВАЮТСЯ КАК ЧИСЛА. По строкам «1.0.9» больше «1.0.10», и
 * половина учеников осталась бы без обновления, а другая увидела бы экран на
 * ровном месте.
 *
 * ⚠️ МУСОР НИЧЕГО НЕ БЛОКИРУЕТ. Ошибка в настройке не должна запирать всю школу
 * за экраном «обновите приложение»: не разобрали — значит не требуем.
 */
export function isBelowMinVersion(current: string, min: string | undefined): boolean {
  return compareVersions(current, min) === -1;
}

/**
 * Новее ли вышедшая версия, чем установленная, — для мягкой плашки «Вышла новая
 * версия» (15.10.2026). Те же правила: числа как числа, мусор — «нет».
 */
export function isNewerVersion(latest: string | undefined, current: string): boolean {
  return compareVersions(latest, current) === 1;
}

/**
 * Сравнение номеров по частям, как чисел: -1, 0, 1; недостающие части — нули
 * («1.1» = «1.1.0»). `null` — хотя бы одну сторону не разобрать. Та же логика,
 * что `CompareVersions` в users-service (`app_config_validate.go`).
 */
export function compareVersions(a: string | undefined, b: string | undefined): -1 | 0 | 1 | null {
  const x = parseVersion(a);
  const y = parseVersion(b);
  if (!x || !y) return null;

  const length = Math.max(x.length, y.length);
  for (let i = 0; i < length; i += 1) {
    const left = x[i] ?? 0;
    const right = y[i] ?? 0;
    if (left !== right) return left < right ? -1 : 1;
  }
  return 0;
}

function parseVersion(raw: string | undefined): number[] | null {
  const value = (raw ?? '').trim();
  if (!value) return null;
  const parts = value.split('.');
  const nums = parts.map((p) => Number(p));
  if (nums.some((n) => !Number.isFinite(n))) return null;
  return nums;
}

/**
 * Пора ли выйти из аккаунта по требованию сервера.
 *
 * Поколение сессий — просто число в настройках. Увеличили — все выходят один
 * раз. Нужно, когда меняется устройство сессии и старый вход перестаёт
 * работать правильно.
 *
 * ⚠️ ПЕРВЫЙ ЗАПУСК ВЫХОДОМ НЕ СЧИТАЕТСЯ: человек только что вошёл, выкидывать
 * его бессмысленно и обидно.
 */
export function shouldForceLogout(serverEpoch: number | undefined, seenEpoch: number | null): boolean {
  if (typeof serverEpoch !== 'number' || !Number.isFinite(serverEpoch)) return false;
  if (seenEpoch === null) return false;
  return serverEpoch > seenEpoch;
}

/**
 * Версия установленного приложения. В браузере её нет и быть не может —
 * там всегда свежая страница, обновлять нечего.
 */
export async function getAppVersion(): Promise<string> {
  if (!isStoreBuild()) return '';
  try {
    const info = await CapacitorApp.getInfo();
    return info.version ?? '';
  } catch {
    return '';
  }
}

const SEEN_EPOCH_KEY = 'onebilim.sessionEpoch';

export function readSeenEpoch(): number | null {
  try {
    const raw = localStorage.getItem(SEEN_EPOCH_KEY);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function rememberEpoch(epoch: number): void {
  try {
    localStorage.setItem(SEEN_EPOCH_KEY, String(epoch));
  } catch {
    /* приватный режим — переживём, выход просто не повторится */
  }
}
